import { EnableMicrophone, Icons, PedagogyUtils, Switch } from "@kalyzee/escaet-ui";
import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { Meeting, MeetingSession } from "@kalyzee/kast-web-sdk/websocket";
import { useEffect, useRef } from "react";
import MeetingPlayerSoundEffectOverlay from "../../../components/player/MeetingPlayerSoundEffectOverlay";
import { PlayersGrid, PlayersGridRef } from "../../../components/player/PlayersGrid";
import MeetingPlayerStyleContainer from "../../../components/player/container/MeetingPlayerStyleContainer";
import { PlayerItem } from "../../../components/player/interfaces";
import { useMeetingSelector, usePermission } from "../../../hooks/meeting";
import { PopulatedMeetingData } from "../../../interfaces/populatedData";
import { PedagogyContextState } from "../../../pages/master/pedagogy/context";
import styles from "./expertDashboardContainer.module.css";
import { MeetingSessionOvenMediaReceiver } from "@kalyzee/kast-web-sdk";
import { useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { toastInfo } from "../../../helpers/toast";

const RestrictedAudioPermission = "[[role;>=;moderator]||[highlighting;==;ongoing]]";

export interface PedagogyExpertDashboardContainerProps {
  meetingContainerAPI?: MeetingContainerAPI;
  context: PedagogyContextState | undefined;
  meeting?: Meeting;
  populatedMeetingData?: PopulatedMeetingData;
  ongoingGroups?: boolean;
  onClick?: (session: MeetingSession) => void;
  audioValidator?: (session: MeetingSessionOvenMediaReceiver) => boolean;
  style?: React.CSSProperties;
  className?: string;
}
export const PedagogyExpertDashboardContainer = ({
  meetingContainerAPI,
  meeting,
  context,
  ongoingGroups,
  onClick,
  audioValidator,
  style,
  className,
}: PedagogyExpertDashboardContainerProps) => {
  const permissionAPI = usePermission(meeting);
  const gridRef = useRef<PlayersGridRef>(null);
  const [receptionSessions] = useMeetingSelector(meetingContainerAPI, (state) => state.receptionSessions);
  const contextRef = useRefAutoUpdate(context);

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    if (!audioValidator) return;
    const validator = contextRef.current?.addAudioValidator((session) => {
      return audioValidator(session);
    });
    return () => {
      validator?.flush();
    };
  }, [audioValidator]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  if (!meeting) return null;

  const sessions = context?.orderedSessions ?? [];
  const rows = 3;
  const columns = 3;

  const renderButtons = () => {
    const checked = !permissionAPI.hasPermission("audio", RestrictedAudioPermission);
    return (
      <div className={styles.buttonContainer} onClick={() => {
        if (ongoingGroups) {
          toastInfo("Des groupes sont encore formés. Cette fonctionnalité est accessible uniquement en mode cours.");
        }
      }}>
        <PedagogyUtils.EnableMicrophone
          disabled={ongoingGroups}
          style={{ opacity: ongoingGroups ? 0.4 : 1 }}
          isMicrophoneActive={checked}
          onMicrophoneStatusChange={() => permissionAPI.setPermission("audio", RestrictedAudioPermission, checked)}
        />
      </div>
    );
  };

  return (
    <div className={`${styles.container}${className ? ` ${className}` : ""}`} style={style}>
      <div className={styles.content}>
        {sessions.length ? (
          <PlayersGrid
            ref={gridRef}
            name="STANDALONE"
            style={{ width: "100%", padding: "20px", flex: "1", minHeight: "0px", fontSize: "16px" }}
            meeting={meeting}
            gridProps={{
              column: columns,
              row: rows,
              autoAdjust: false,
            }}
            items={
              sessions?.map(
                (s) =>
                  new PlayerItem({
                    type: "meeting_session",
                    meetingSession: s.session,
                    videoSessions: receptionSessions?.filter((s2) => s2.data.session.id === s.session.id) ?? [],
                    online: s.connected,
                  })
              ) ?? []
            }
            renderItem={(renderDefaultPlayer, item) => {
              const itemData = item?.getData();
              if (itemData?.type === "meeting_session") {
                const clickable = itemData.online !== false;
                return renderDefaultPlayer((children, context) => (
                  <MeetingPlayerStyleContainer
                    mode="dashboard"
                    meeting={meeting}
                    session={itemData.meetingSession}
                    style={{ width: "100%", height: "100%", cursor: clickable ? "pointer" : undefined }}
                    isPlayingSound={context?.isPlayingSound}
                    nativeProps={{
                      onClick: () => {
                        if (!clickable) return;
                        onClick?.(itemData.meetingSession);
                      },
                    }}
                  >
                    {children}
                    <MeetingPlayerSoundEffectOverlay enabled={context?.isPlayingSound} />
                  </MeetingPlayerStyleContainer>
                ));
              }
              return <div />;
            }}
            defaultRendererProviderOptions={{
              muted: true,
              hideSoundEffect: true,
            }}
            autoOnOffVideo={true}
          />
        ) : (
          <div>Aucun participant</div>
        )}
      </div>

      {renderButtons()}
    </div>
  );
};
